import z from "zod";
import createSchemaUtils from "../../helpers/createSchemaUtils";
import { PlainObjectSchema } from "../PlainObject";
import { SafeNameSchema } from "../common";

//#region QueryFilterItem
export const QueryFilterItemSchema = z.object({
    field: SafeNameSchema,
    operator: z.enum([
        "isNull",
        "notNull",
        "eq",
        "ne",
        "gt",
        "ge",
        "in",
        "notin",
        "lt",
        "le",
        "contains",
        "startswith",
        "endswith"]).default("eq"),
    value: z.any(),
});
export type QueryFilterItem = z.infer<typeof QueryFilterItemSchema>;
export const QueryFilterItem = createSchemaUtils(QueryFilterItemSchema);
//#endregion

//#region QueryFilterDeclarative
export const QueryFilterDeclarativeSchema = z.object({
    name: SafeNameSchema,
    parameters: PlainObjectSchema.optional(),
});
/**
 * A declarative filter that can be resolved on the server side based on predefined filter logic.
 * USAGE:
 * ```typescript
 * const filter: QueryFilterDeclarative = {
 *     name: "MyActiveWorkItemsFilter",
 *     parameters: {
 *         id: "MyPersonId-12345"
 *     }
 * };
 * // This filter can then be interpreted by the server to apply specific filtering logic
 * // based on the "MyActiveWorkItemsFilter" definition and the provided parameters.
 * // For example, somewhere in the server code:
 * if(filter.name === "MyActiveWorkItemsFilter") {
 *     return `workitem.owner eq ${filter.parameters?.id} AND workitem.status eq 'active'`;
 * }
 * ```
 */
export type QueryFilterDeclarative = z.infer<typeof QueryFilterDeclarativeSchema>;
export const QueryFilterDeclarative = createSchemaUtils(QueryFilterDeclarativeSchema);
//#endregion

//#region QueryFilterGroup
export const QueryFilterGroupSchema: z.ZodType<QueryFilterGroup> = z.lazy(() => z.object({
    and: z.boolean().default(true).optional(),
    filters: z.union([
        QueryFilterItemSchema,
        QueryFilterGroupSchema,
        QueryFilterDeclarativeSchema
    ]).array().nonempty(),
}));
export type QueryFilterGroup = {
    and?: boolean;
    filters: Array<QueryFilterItem | QueryFilterGroup | QueryFilterDeclarative>;
};
export const QueryFilterGroup = createSchemaUtils(QueryFilterGroupSchema);
//#endregion


//#region QueryFilter
export const QueryFilterSchema: z.ZodType<QueryFilterItem | QueryFilterGroup | QueryFilterDeclarative> = z.union([
    QueryFilterItemSchema,
    QueryFilterGroupSchema,
    QueryFilterDeclarativeSchema
]);
export type QueryFilter = z.infer<typeof QueryFilterSchema>;
export const QueryFilter = createSchemaUtils(QueryFilterSchema);
//#endregion
